import { useId } from 'react';
import type { ComponentPropsWithRef } from 'react';

import { formatPercentage } from '../../i18n/format';
import { cn } from '../../lib/cn';

export type ProgressVariant = 'circular' | 'linear';
export type ProgressSize = 'sm' | 'md' | 'lg' | 'xl';
export type ProgressTone = 'primary' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

/** The circular ring paints with `currentColor`, so the tone is a text token on
 *  the wrapper rather than an inline colour. */
const TONE_TEXT: Record<ProgressTone, string> = {
  primary: 'text-primary',
  accent: 'text-accent',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  info: 'text-info',
};

/** The linear fill is its own box, so it needs the background form of the same
 *  token — same colour, same theme flip. */
const TONE_FILL: Record<ProgressTone, string> = {
  primary: 'bg-primary',
  accent: 'bg-accent',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
};

const LINEAR_HEIGHT: Record<ProgressSize, string> = {
  sm: 'h-1',
  md: 'h-2',
  lg: 'h-3',
  xl: 'h-4',
};

const CIRCULAR_SIZE: Record<ProgressSize, string> = {
  sm: 'size-6',
  md: 'size-9',
  lg: 'size-12',
  xl: 'size-16',
};

const VALUE_TEXT: Record<ProgressSize, string> = {
  sm: 'text-2xs',
  md: 'text-xs',
  lg: 'text-sm',
  xl: 'text-base',
};

/** No `tabular-nums`: `font-display` is Oswald, which ships no `tnum` feature —
 *  the declaration was inert (see the note beside `--font-display` in
 *  tokens.css). Under the display face the figure is proportional; the value is
 *  right-aligned in its row, so the digits move but the layout does not. */
const EMPHASIS = 'font-display font-semibold text-heading';

/** Ring geometry. The viewBox is fixed and the size class scales the rendered
 *  box, so the arc maths never depends on the pixel size. */
const VIEWBOX = 40;
const RADIUS = 17;
const STROKE = 4;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export interface ProgressProps extends Omit<ComponentPropsWithRef<'div'>, 'children'> {
  variant?: ProgressVariant;
  /** `null`/`undefined` (or a non-finite number) means there is no value to show
   *  yet, which is an indeterminate bar — not a bar sitting at 0%. */
  value?: number | null;
  max?: number;
  indeterminate?: boolean;
  size?: ProgressSize;
  tone?: ProgressTone;
  /** Accessible name. Rendered as the visible label as well. */
  label?: string;
  showValue?: boolean;
}

/**
 * Circular and linear progress.
 *
 * Indeterminate is the loading state, not decoration: the loop is attached
 * whenever the value is missing, at every animation level. The spinner is the
 * house precedent for the reduced-motion policy — `global.css` freezes the
 * keyframe globally, and the bar is left as a static partial fill that still
 * reads as "working".
 */
export function Progress({
  variant = 'linear',
  value = null,
  max = 100,
  indeterminate = false,
  size = 'md',
  tone = 'primary',
  label,
  showValue = false,
  className,
  ...rest
}: ProgressProps) {
  const labelId = `${useId()}-label`;

  const safeMax = Number.isFinite(max) && max > 0 ? max : 100;
  // `Number.isFinite`, never `Number.isNaN` — the latter reports `false` for
  // `null`, `undefined` and non-numbers, which would let an empty value through
  // as a determinate bar pinned at 0%.
  const hasValue = typeof value === 'number' && Number.isFinite(value);
  const isIndeterminate = indeterminate || !hasValue;
  const clamped = hasValue ? Math.min(Math.max(value, 0), safeMax) : 0;
  const percent = (clamped / safeMax) * 100;
  const valueText = !isIndeterminate && showValue ? formatPercentage(percent, { precision: 0 }) : null;

  const trackProps = isIndeterminate
    ? { role: 'progressbar' as const, 'aria-busy': true }
    : {
        role: 'progressbar' as const,
        'aria-valuenow': clamped,
        'aria-valuemin': 0,
        'aria-valuemax': safeMax,
      };

  const labelledBy = label ? { 'aria-labelledby': labelId } : {};
  const labelNode = label ? (
    <span
      id={labelId}
      className="font-display text-2xs font-semibold uppercase tracking-label text-muted"
    >
      {label}
    </span>
  ) : null;
  // Already announced by `aria-valuenow`, so keep it out of the reading order.
  const valueNode = valueText ? (
    <span className={cn('shrink-0', EMPHASIS, VALUE_TEXT[size])} aria-hidden="true">
      {valueText}
    </span>
  ) : null;

  if (variant === 'circular') {
    const dashOffset = CIRCUMFERENCE * (1 - percent / 100);

    return (
      <div {...rest} className={cn('inline-flex items-center gap-3', TONE_TEXT[tone], className)}>
        <span
          {...trackProps}
          {...labelledBy}
          className={cn('relative inline-grid shrink-0 place-items-center', CIRCULAR_SIZE[size])}
        >
          <svg
            viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
            aria-hidden="true"
            focusable="false"
            className={cn(
              'size-full -rotate-90',
              isIndeterminate && 'dhd-indeterminate-ring animate-[dhd-spin_0.9s_linear_infinite]',
            )}
          >
            <circle
              cx={VIEWBOX / 2}
              cy={VIEWBOX / 2}
              r={RADIUS}
              fill="none"
              strokeWidth={STROKE}
              className="stroke-line"
            />
            <circle
              cx={VIEWBOX / 2}
              cy={VIEWBOX / 2}
              r={RADIUS}
              fill="none"
              strokeWidth={STROKE}
              // Square cap. `RadialGauge` states the rule the whole library
              // follows — "the Constructivist language has no rounded ends" —
              // and a round-capped Progress arc beside a square-capped gauge was
              // two answers to the same question.
              strokeLinecap="butt"
              className={cn(
                'stroke-current',
                !isIndeterminate &&
                  'transition-[stroke-dashoffset] duration-[var(--dur-base)] ease-[var(--ease-out)]',
              )}
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={isIndeterminate ? CIRCUMFERENCE * 0.75 : dashOffset}
            />
          </svg>
        </span>

        {labelNode || valueNode ? (
          <span className="flex min-w-0 flex-col items-start gap-0.5">
            {labelNode}
            {valueNode}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div {...rest} className={cn('flex w-full flex-col gap-2', className)}>
      {labelNode || valueNode ? (
        <div className={cn('flex items-baseline gap-3', label ? 'justify-between' : 'justify-end')}>
          {labelNode}
          {valueNode}
        </div>
      ) : null}

      <div
        {...trackProps}
        {...labelledBy}
        // `--border` is the token the system reserves for the boundary of a
        // control (SC 1.4.11); `--surface-muted` is the only neutral that keeps
        // the tone fill legible against it in both themes.
        //
        // Square ends, matching the circular variant's `strokeLinecap="butt"`
        // and `RadialGauge`'s square cap: the language has no rounded ends.
        className={cn(
          'relative w-full overflow-hidden rounded-none border border-line bg-surface-muted',
          LINEAR_HEIGHT[size],
          // Under reduced motion the fill is hidden and the track itself carries
          // the "no value" signal — see global.css. Without this the frozen
          // keyframe left the bar at a fraction that read as a measurement.
          isIndeterminate && 'dhd-indeterminate-track',
        )}
      >
        {isIndeterminate ? (
          <div
            className={cn(
              'dhd-indeterminate-fill h-full w-full origin-left animate-[dhd-indeterminate_1.4s_ease-in-out_infinite]',
              TONE_FILL[tone],
            )}
          />
        ) : (
          <div
            className={cn(
              'h-full transition-[width] duration-[var(--dur-base)] ease-[var(--ease-out)]',
              TONE_FILL[tone],
            )}
            style={{ width: `${percent}%` }}
          />
        )}
      </div>
    </div>
  );
}
